import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Loader2, Search } from 'lucide-react';
import { toast } from 'sonner';
import { Card, inputCls, goldBtn } from '../ui';
import { api, eur, errMsg } from '../api';
import { usePortal } from '../PortalAuth';
import NegativeList from '../negativita/NegativeList';

function NegativeForm({ initial, enabled, prices, onDone }) {
  const { refresh } = usePortal();
  const [form, setForm] = useState({ name: initial.name || '', tax_code: initial.code || '' });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post('/negative/requests', form);
      toast.success('Ricerca negatività avviata');
      setForm({ name: '', tax_code: '' });
      refresh();
      onDone();
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <Card className="p-6 mb-8" data-testid="negative-form">
      <p className="text-[11px] uppercase tracking-[0.18em] text-[#5A5F67] mb-4">Nuova ricerca su persona fisica</p>
      <form onSubmit={submit} className="grid gap-3 md:grid-cols-[1.4fr_1fr_auto] items-end">
        <input className={`${inputCls} w-full px-3 border`} placeholder="Nome e cognome" value={form.name} onChange={set('name')} required minLength={2} data-testid="negative-name-input" />
        <input className={`${inputCls} w-full px-3 border font-mono uppercase`} placeholder="Codice fiscale (16 caratteri)" value={form.tax_code} onChange={set('tax_code')} required maxLength={16} data-testid="negative-cf-input" />
        <button type="submit" disabled={!enabled || busy} className={`${goldBtn} h-11 px-5 inline-flex items-center justify-center gap-2 disabled:opacity-40`} data-testid="negative-submit-btn">
          {busy ? <Loader2 size={15} className="animate-spin" /> : <Search size={15} />}
          {enabled ? `Cerca${prices.negative_search ? ` · ${eur(prices.negative_search.cents)}` : ''}` : 'In attivazione'}
        </button>
      </form>
      <p className="text-xs text-[#5A5F67] mt-3" data-testid="negative-price-note">
        Verifica protesti, pregiudizievoli di Conservatoria e procedure concorsuali. La ricerca è sempre addebitata; se l'esito è positivo puoi aprire il dettaglio{prices.negative_details ? ` a ${eur(prices.negative_details.cents)}` : ''}.
      </p>
    </Card>
  );
}

export function NegativitaBody() {
  const [params] = useSearchParams();
  const [prices, setPrices] = useState({});
  const [enabled, setEnabled] = useState(false);
  const [items, setItems] = useState(null);
  const load = useCallback(() => api.get('/negative/requests').then((r) => setItems(r.data)).catch(() => setItems([])), []);
  useEffect(() => {
    api.get('/cs/prices').then((r) => setPrices(r.data)).catch(() => {});
    api.get('/negative/status').then((r) => setEnabled(r.data.enabled)).catch(() => {});
    load();
  }, [load]);
  const busy = (items || []).some((r) => r.status === 'pending');
  useEffect(() => {
    if (!busy) return undefined;
    const t = setInterval(load, 8000);
    return () => clearInterval(t);
  }, [busy, load]);
  return (
    <div data-testid="negativita-page">
      <p className="text-sm text-[#8A8F97] mb-4">Qualsiasi persona fisica, anche senza cariche in società: protesti, pregiudizievoli di Conservatoria e procedure concorsuali.</p>
      <NegativeForm initial={{ name: params.get('name'), code: params.get('code') }} enabled={enabled} prices={prices} onDone={load} />
      <NegativeList items={items} setItems={setItems} detailPrice={prices.negative_details?.cents} />
    </div>
  );
}
